import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Search, User, Settings, FileText, FolderOpen, X, CheckCircle, AlertCircle, Info, LogOut, Shield, HelpCircle } from 'lucide-react';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const searchRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);


  // 알림 데이터
  const notifications = [
    {
      id: 1,
      type: 'success',
      title: '새 공모사업 발견',
      message: 'NIPA AI 융합 서비스 개발 지원사업이 공고되었습니다.',
      time: '5분 전',
      icon: CheckCircle,
      read: false
    },
    {
      id: 2,
      type: 'warning',
      title: '마감 임박',
      message: '콘텐츠진흥원 XR 콘텐츠 제작 지원 마감 D-3',
      time: '1시간 전',
      icon: AlertCircle,
      read: false
    },
    {
      id: 3,
      type: 'info',
      title: '매칭 추천',
      message: '(주)테크솔루션에 적합한 공모사업 3건이 발견되었습니다.',
      time: '2시간 전',
      icon: Info,
      read: true
    }
  ];

  const unreadCount = notifications.filter(n => !n.read).length;

  // 사용자 메뉴 옵션
  const userMenuOptions = [
    { label: '프로필', icon: User, action: () => console.log('프로필 열기') },
    { label: '보안', icon: Shield, action: () => console.log('보안 설정') },
    { label: '도움말', icon: HelpCircle, action: () => console.log('도움말') },
    { label: '로그아웃', icon: LogOut, action: () => console.log('로그아웃') },
  ];

  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const token = localStorage.getItem('proposalcraft_access_token');
        const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
          ? 'http://localhost:8001' : `${window.location.protocol}//${window.location.hostname}:8001`;

        const response = await fetch(
          `${baseUrl}/api/public-grants/?search=${encodeURIComponent(searchQuery)}&limit=5`,
          { headers: token ? { 'Authorization': `Bearer ${token}` } : {} }
        );
        if (response.ok) {
          const data = await response.json();
          setSearchResults((data.items || []).map((g: any) => ({
            id: g.id,
            title: g.title,
            type: g.source || '공모사업',
            category: g.category || '',
            icon: FolderOpen,
          })));
          setShowSearchResults(true);
        }
      } catch {
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearchResults(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // 실제 검색 로직 구현
      console.log('Searching for:', searchQuery);
    }
  };

  const handleResultClick = (result: any) => {
    setSearchQuery('');
    setShowSearchResults(false);
    // 결과에 따른 네비게이션 로직
    if (result.type === 'project') {
      window.location.href = `/project/${result.id}`;
    } else if (result.type === 'template') {
      window.location.href = '/templates';
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success':
        return CheckCircle;
      case 'warning':
        return AlertCircle;
      case 'info':
        return Info;
      default:
        return Info;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'success':
        return 'text-green-500';
      case 'warning':
        return 'text-yellow-500';
      case 'info':
        return 'text-blue-500';
      default:
        return 'text-gray-500';
    }
  };

  const handleUserMenuClick = (option: any) => {
    option.action();
    setShowUserMenu(false);
  };

  return (
    <header className="bg-white border-b border-gray-200 px-6 py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h1 className="text-xl font-semibold text-gray-900">
            ProposalCraft AI
          </h1>
          <div className="relative" ref={searchRef}>
            <form onSubmit={handleSearchSubmit}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="공모사업, 기업, 제안서 검색..."
                  className="pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent w-80"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </form>
            
            {/* 검색 결과 드롭다운 */}
            {showSearchResults && searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
                {searchResults.map((result) => {
                  const Icon = result.icon;
                  return (
                    <button
                      key={result.id}
                      onClick={() => handleResultClick(result)}
                      className="w-full p-3 hover:bg-gray-50 text-left flex items-center space-x-3 transition-colors"
                    >
                      <Icon className="h-4 w-4 text-gray-500" />
                      <div>
                        <div className="font-medium text-gray-900">
                          {result.title}
                        </div>
                        <div className="text-sm text-gray-500 capitalize">{result.type}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="relative" ref={notificationsRef}>
            <button 
              onClick={() => setShowNotifications(!showNotifications)}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors relative"
            >
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>
            
            {/* 알림 드롭다운 */}
            {showNotifications && (
              <div className="absolute top-full right-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">알림</h3>
                    <button className="text-sm text-primary-600 hover:text-primary-700">
                      모두 읽음으로 표시
                    </button>
                  </div>
                </div>
                <div className="divide-y divide-gray-100">
                  {notifications.map((notification) => {
                    const Icon = getNotificationIcon(notification.type);
                    const iconColor = getNotificationColor(notification.type);
                    return (
                      <div 
                        key={notification.id} 
                        className={`p-4 hover:bg-gray-50 transition-colors ${!notification.read ? 'bg-blue-50' : ''}`}
                      >
                        <div className="flex items-start space-x-3">
                          <Icon className={`h-5 w-5 mt-0.5 ${iconColor}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                            <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                            <p className="text-xs text-gray-500 mt-2">{notification.time}</p>
                          </div>
                          {!notification.read && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="p-4 border-t border-gray-200">
                  <button className="w-full text-center text-sm text-primary-600 hover:text-primary-700">
                    모든 알림 보기
                  </button>
                </div>
              </div>
            )}
          </div>
          <button 
            onClick={() => navigate('/settings')}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            title="설정"
          >
            <Settings className="h-5 w-5" />
          </button>
          <div className="relative" ref={userMenuRef}>
            <button 
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center space-x-2 p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                <User className="h-4 w-4 text-primary-600" />
              </div>
              <span className="text-sm font-medium">사용자</span>
            </button>
            
            {/* 사용자 메뉴 드롭다운 */}
            {showUserMenu && (
              <div className="absolute top-full right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <User className="h-5 w-5 text-primary-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">홍길동</p>
                      <p className="text-xs text-gray-500">hong@example.com</p>
                    </div>
                  </div>
                </div>
                <div className="py-2">
                  {userMenuOptions.map((option, index) => {
                    const Icon = option.icon;
                    return (
                      <button
                        key={index}
                        onClick={() => handleUserMenuClick(option)}
                        className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <Icon className="h-4 w-4" />
                        <span>{option.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header; 